/**
 * Boot-time session restoration.
 *
 * Called once from `App.vue`. On a fresh tab the in-memory access token
 * is gone, but the HttpOnly refresh cookie may still be valid. We try a
 * silent refresh through the api client's single-flight coordinator
 * (same one the 401-retry path uses), then follow up with `/auth/me`
 * to fill in user + sites.
 *
 * Why the coordinator: backend uses a rotating-refresh-token reuse
 * detector that revokes the whole token family on a second presentation
 * of an already-rotated RT (`ezjwt_refresh_reuse`). If bootstrap calls
 * `refreshRequest` directly while a concurrent 401-driven refresh is
 * also in flight, both fire the same RT and one of them comes back
 * with the reuse error — taking the whole session down. The shared
 * coordinator dedupes to exactly one inflight refresh and hands the
 * same promise to every caller.
 *
 * Either step failing because the server *rejected* it leaves the
 * session cleared — the user sees the login screen on the next route.
 * A step that never reached a server verdict (offline, timeout, or a
 * CORS-blocked refresh preflight) is transient: we leave the session
 * untouched rather than logging the user out on a flaky network.
 */
import { ref } from 'vue'

import { ApiError } from '@/shared/api'
import { getApiClient } from '@/app/providers/api'
import { meRequest } from '@/features/auth/api/auth.api'
import { useSessionStore } from '@/app/stores/session.store'

export function useSession() {
  const session = useSessionStore()
  const isBootstrapping = ref(false)

  async function bootstrap(): Promise<void> {
    if (isBootstrapping.value) return
    isBootstrapping.value = true
    try {
      const api = getApiClient()
      // Single-flight refresh — see file docblock for the why. The
      // thunk inside `getApiClient` writes the tokens into the session
      // store; we only need to react to the ok/failed verdict here.
      const verdict = await api.refreshSession()
      if (verdict === 'failed') {
        // Server rejected the refresh (expired/revoked RT) → real logout.
        session.clear()
        return
      }
      if (verdict === 'unreachable') {
        // Couldn't reach the server for a verdict (offline / CORS-blocked
        // preflight). Don't clear — the RT cookie may still be valid; a
        // later route navigation will retry once connectivity is back.
        return
      }

      const me = await meRequest(api)
      session.setProfile({ user: me.user, sites: me.sites })
    } catch (e) {
      // ApiError from the `/auth/me` step: a 401/403 means the
      // freshly-refreshed session was rejected → logout. A transport
      // failure (network/timeout — incl. CORS) is transient, so leave
      // the session alone. Non-ApiError is unexpected → surface it.
      if (!(e instanceof ApiError)) {
        throw e
      }
      if (e.kind === 'network' || e.kind === 'timeout') {
        return
      }
      session.clear()
    } finally {
      isBootstrapping.value = false
    }
  }

  return { bootstrap, isBootstrapping }
}
